import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { User, AuthState } from '../types';

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Mock credentials — replace with real API call
const MOCK_CREDENTIALS = { email: 'admin@auralglobal.com', password: 'aural2025' };
const MOCK_USER: User = { id: '1', name: 'Admin User', email: 'admin@auralglobal.com', role: 'admin' };
const TOKEN_KEY = 'aural_admin_token';
const USER_KEY = 'aural_admin_user';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    const userRaw = sessionStorage.getItem(USER_KEY);
    const user = userRaw ? JSON.parse(userRaw) as User : null;
    return { user, token, isAuthenticated: !!token, isLoading: false };
  });

  const login = useCallback(async (email: string, password: string) => {
    setState(s => ({ ...s, isLoading: true }));
    // Simulate network delay
    await new Promise(r => setTimeout(r, 900));

    if (email === MOCK_CREDENTIALS.email && password === MOCK_CREDENTIALS.password) {
      const token = btoa(`${email}:${Date.now()}`);
      sessionStorage.setItem(TOKEN_KEY, token);
      sessionStorage.setItem(USER_KEY, JSON.stringify(MOCK_USER));
      setState({ user: MOCK_USER, token, isAuthenticated: true, isLoading: false });
    } else {
      setState(s => ({ ...s, isLoading: false }));
      throw new Error('Invalid email or password');
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    setState({ user: null, token: null, isAuthenticated: false, isLoading: false });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
